// Two-row grouped tab navigation.
//
// Primary row: Today | Plan | Build | Money | People | Ops (7 items).
// Secondary row: only the active group's children, rendered visually
// subordinate. Today is special — no secondary row when active.
//
// Mobile (<768px): primary row collapses to a <select> dropdown showing the
// current group label. Secondary row stays as horizontal scroll.

import type { Tab } from './Header'
import {
  TAB_GROUPS,
  type GroupId,
  type TabGroup,
} from '../lib/tab-groups'

type Props = {
  activeGroup: GroupId | 'today'
  activeTab: Tab
  onPickGroup: (g: GroupId | 'today') => void
  onPickTab: (t: Tab) => void
}

export default function TabNav({ activeGroup, activeTab, onPickGroup, onPickTab }: Props) {
  const activeGroupDef: TabGroup | null =
    activeGroup === 'today' ? null : TAB_GROUPS.find((g) => g.id === activeGroup) ?? null

  return (
    <nav className="tabnav">
      {/* Primary row — desktop */}
      <div className="tabnav-primary tabnav-desktop">
        <button
          className={`tabnav-primary-btn ${activeGroup === 'today' ? 'active' : ''}`}
          onClick={() => onPickGroup('today')}
        >
          Today
        </button>
        {TAB_GROUPS.map((g) => (
          <button
            key={g.id}
            className={`tabnav-primary-btn ${activeGroup === g.id ? 'active' : ''}`}
            onClick={() => onPickGroup(g.id)}
          >
            {g.label}
          </button>
        ))}
      </div>

      {/* Primary row — mobile collapse to dropdown */}
      <div className="tabnav-primary tabnav-mobile">
        <select
          className="tabnav-mobile-select"
          value={activeGroup}
          onChange={(e) => onPickGroup(e.target.value as GroupId | 'today')}
        >
          <option value="today">Today</option>
          {TAB_GROUPS.map((g) => (
            <option key={g.id} value={g.id}>
              {g.label}
            </option>
          ))}
        </select>
      </div>

      {/* Secondary row — only when a group is active */}
      {activeGroupDef && (
        <div className="tabnav-secondary">
          {activeGroupDef.tabs.map((t) => (
            <button
              key={t.id}
              className={`tabnav-secondary-btn ${activeTab === t.id ? 'active' : ''}`}
              onClick={() => onPickTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
    </nav>
  )
}
